import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  getCampaign,
  exportCampaignCsv,
  getWhatsAppErrorMessage,
} from "../../services/whatsappApi";
import {
  campaignStatusClass,
  campaignProgressPercent,
  formatCampaignDate,
  downloadBlob,
} from "../../utils/whatsappCampaign";
import { formatWhatsAppMobile } from "../../utils/whatsappFormat";
import "./whatsapp.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const POLL_MS = 15000;

function reportError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

function StatusBadge({ status }) {
  return (
    <span className={`wa-status-badge ${campaignStatusClass(status)}`}>
      {status || "—"}
    </span>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="whatsapp-stat-card">
      <div className="whatsapp-stat-card-icon">
        <Icon icon={icon} width={22} />
      </div>
      <div>
        <p className="whatsapp-stat-card-label">{label}</p>
        <p className="whatsapp-stat-card-value">{value ?? 0}</p>
      </div>
    </div>
  );
}

export default function WhatsAppCampaignDetail() {
  const { id } = useParams();
  const pollRef = useRef(null);

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchDetail = useCallback(
    async ({ silent = false } = {}) => {
      if (!id) return;
      if (!silent) {
        setLoading(true);
        setError("");
      }
      try {
        const res = await getCampaign(id);
        const data = res?.data ?? res;
        setCampaign(data);
      } catch (err) {
        const msg = reportError(err, "Failed to load campaign");
        if (!silent) {
          setError(msg);
          setCampaign(null);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const status = String(campaign?.status || "").toUpperCase();
    if (status === "PENDING" || status === "IN_PROGRESS") {
      pollRef.current = setInterval(() => {
        fetchDetail({ silent: true });
      }, POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [campaign?.status, fetchDetail]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportCampaignCsv(id);
      const safeTitle = String(campaign?.title || "campaign")
        .replace(/[^\w\-]+/g, "_")
        .slice(0, 40);
      downloadBlob(blob, `${safeTitle}_export.csv`);
      toast.success("Export downloaded");
    } catch (err) {
      reportError(err, "Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const logs = Array.isArray(campaign?.logs) ? campaign.logs : [];
  const progress = campaignProgressPercent(campaign);

  if (loading) {
    return (
      <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel">
          <div className="whatsapp-page-skeleton p-3">
            {[1, 2, 3, 4].map((k) => (
              <div
                key={k}
                className="whatsapp-skeleton"
                style={{ height: 72, marginBottom: 12 }}
              />
            ))}
          </div>
        </div>
      </div>
      </WhatsAppPageShell>
    );
  }

  if (error || !campaign) {
    return (
      <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel">
          <div className="whatsapp-list-error">{error || "Campaign not found"}</div>
          <Link to="/whatsapp/campaigns" className="whatsapp-back-link mt-3 d-inline-flex">
            <Icon icon="solar:arrow-left-line-duotone" width={18} />
            Back to My Campaign
          </Link>
        </div>
      </div>
      </WhatsAppPageShell>
    );
  }

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
    <div className="whatsapp-inbox-root">
      <div className="whatsapp-page-panel">
        <header className="whatsapp-page-header">
          <div>
            <Link to="/whatsapp/campaigns" className="whatsapp-back-link">
              <Icon icon="solar:arrow-left-line-duotone" width={18} />
              Back to My Campaign
            </Link>
            <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
              <h1 className="mb-0">{campaign.title}</h1>
              <StatusBadge status={campaign.status} />
            </div>
            <p className="whatsapp-page-subtitle">
              Created {formatCampaignDate(campaign.createdAt)}
            </p>
          </div>
          <button
            type="button"
            className="whatsapp-btn-secondary"
            onClick={handleExport}
            disabled={exporting}
          >
            <Icon icon="solar:download-line-duotone" width={18} />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </header>

        <div className="whatsapp-stat-grid">
          <StatCard
            label="Total contacts"
            value={campaign.totalContacts}
            icon="solar:users-group-rounded-line-duotone"
          />
          <StatCard
            label="Sent"
            value={campaign.sentCount}
            icon="solar:letter-line-duotone"
          />
          <StatCard
            label="Failed"
            value={campaign.failedCount}
            icon="solar:close-circle-line-duotone"
          />
          <StatCard
            label="Delivered"
            value={campaign.deliveredCount}
            icon="solar:check-circle-line-duotone"
          />
          <StatCard
            label="Read"
            value={campaign.readCount}
            icon="solar:eye-line-duotone"
          />
        </div>

        <div className="whatsapp-progress-card">
          <div className="d-flex justify-content-between mb-2">
            <span className="fw-medium">Progress</span>
            <span className="text-muted">{progress}%</span>
          </div>
          <div className="whatsapp-progress-bar">
            <div
              className="whatsapp-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="whatsapp-page-body">
          <h2 className="whatsapp-section-title">Delivery logs</h2>
          {logs.length === 0 ? (
            <div className="whatsapp-list-empty">No logs yet.</div>
          ) : (
            <div className="whatsapp-contacts-table-wrap">
              <table className="whatsapp-contacts-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Mobile</th>
                    <th>Status</th>
                    <th>Delivery</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={`${log.contactMobile}-${idx}`}>
                      <td>{log.contactName || "—"}</td>
                      <td>
                        {formatWhatsAppMobile(log.contactMobile) ||
                          log.contactMobile ||
                          "—"}
                      </td>
                      <td>
                        <StatusBadge status={log.status} />
                      </td>
                      <td>{log.deliveryStatus || "—"}</td>
                      <td className="text-danger small">
                        {log.errorMessage || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
    </WhatsAppPageShell>
  );
}
